// =============================================================================
// Inventory Management — the snapshot operations opens first.
//
// Ordered by what changes a decision today: the outbound funnel sits near the top because a pallet that
// has been allocated for weeks and never shipped is the thing worth chasing, while the procurement
// totals below it have not moved since the purchase orders were signed.
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { strings } from '/imports/strings';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { useMethod } from '/imports/hooks';
import {
  inventoryDashboard, inventoryDeliveryStatusLabel, inventoryPalletStatusLabel, inventoryShipmentStatusLabel,
} from '/imports/api/inventory';
import {
  EmptyRow, InventoryPageStyled, Tile, count, money, orDash, useInventoryAccess,
} from '/imports/ui/InventoryShared';

/** Portfolio-wide inventory snapshot: stock, procurement, demand and the outbound queue. */
export const InventoryDashboardPage = () => {
  const { canAccess } = useInventoryAccess();
  const [data, loading] = useMethod(inventoryDashboard, undefined, []);
  if (!canAccess) return <NotPermittedPage />;

  return (
    <InventoryPageStyled className="page">
      <PageHeading left={<h1>{strings.Inventory()}</h1>} />
      <PageBody>
        {loading && !data && <LoadingFlex />}
        {!loading && !data && <div className="empty">{strings.NoResults()}</div>}
        {data && <>
          <div className="tiles">
            <Tile label={strings.InventoryLineItems()} value={count(data.snapshot.lineItems)} sub={`${count(data.snapshot.pallets)} ${strings.InventoryTotalPallets().toLowerCase()}`} />
            <Tile label={strings.InventoryUnitsOnHand()} value={count(data.snapshot.unitsOnHand)} sub={`${count(data.snapshot.unitsTotal)} ${strings.InventoryTotalUnits().toLowerCase()}`} />
            <Tile label={strings.InventoryOnHandValue()} value={money(data.snapshot.valueOnHand)} sub={strings.InventoryCostBasisExTax()} />
            <Tile label={strings.InventoryGrandTotalInclTax()} value={money(data.procurement.totalCost)} sub={`${strings.InventorySalesTax()} ${money(data.procurement.salesTax)}`} />
            <Tile label={strings.InventoryTotalNameplate()} value={`${data.procurement.mw.toFixed(2)} MW`} sub={`${count(data.procurement.modulesOrdered)} ${strings.InventoryTotalModules().toLowerCase()}`} />
          </div>

          <div className="panels">
            <div className="panel-card">
              <h2>{strings.InventoryOutbound()}</h2>
              <table className="inv">
                <tbody>
                  <tr><td>{strings.InventorySitesWithPallets()}</td><td className="num">{count(data.outbound.sitesWithPallets)}</td></tr>
                  <tr><td>{strings.InventorySitesWithDelivery()}</td><td className="num">{count(data.outbound.sitesWithDelivery)}</td></tr>
                  {data.outbound.byStatus.filter((s) => s.count > 0).map((s) => (
                    <tr key={s.status}><td className="muted">&nbsp;&nbsp;{inventoryDeliveryStatusLabel(s.status)}</td><td className="num">{count(s.count)}</td></tr>
                  ))}
                  <tr><td>{strings.InventoryTrucksRequired()}</td><td className="num">{count(data.outbound.trucksRequired)}</td></tr>
                  <tr><td>{strings.InventoryPalletsAllocatedNotShipped()}</td><td className="num">{count(data.outbound.palletsAllocatedNotShipped)}</td></tr>
                  <tr><td>{strings.InventoryPalletsDelivered()}</td><td className="num">{count(data.outbound.palletsDelivered)}</td></tr>
                  <tr><td>{strings.InventoryModulesDelivered()}</td><td className="num">{count(data.outbound.modulesDelivered)}</td></tr>
                  <tr>
                    <td>{strings.InventorySitesArrivingAfterMobilization()}</td>
                    <td className="num">{data.outbound.sitesArrivingAfterMobilization ? <span className="neg">{count(data.outbound.sitesArrivingAfterMobilization)}</span> : count(0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryStockAllocatedVsAvailable()}</h2>
              <div className="scroll-x">
                <table className="inv">
                  <thead>
                    <tr>
                      <th>{strings.InventoryWarehouse()}</th>
                      <th className="num">{strings.InventoryPalletsOnHand()}</th>
                      <th className="num">{strings.InventoryPalletsAllocated()}</th>
                      <th className="num">{strings.InventoryPalletsAvailable()}</th>
                      <th className="num">{strings.InventoryModulesAvailable()}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byWarehouse.map((w) => (
                      <tr key={w.warehouseId}>
                        <td><Link to={`/inventory/warehouses/${w.warehouseId}`}>{w.code}</Link> <span className="muted">{w.label}</span></td>
                        <td className="num">{count(w.palletsOnHand)}</td>
                        <td className="num">{count(w.palletsAllocated)}</td>
                        <td className="num">{count(w.palletsAvailable)}</td>
                        <td className="num">{count(w.modulesAvailable)}</td>
                      </tr>
                    ))}
                    {!data.byWarehouse.length && <EmptyRow colSpan={5} />}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="panel-card">
              <h2>{strings.InventorySupplyVsDemand()}</h2>
              <table className="inv">
                <tbody>
                  <tr><td>{strings.InventoryTotalSites()}</td><td className="num">{count(data.demand.sites)}</td></tr>
                  <tr><td>{strings.InventoryModulesRequired()}</td><td className="num">{count(data.demand.modulesRequired)}</td></tr>
                  <tr><td className="muted">&nbsp;&nbsp;{strings.InventoryModulesRequiredInProgress()}</td><td className="num">{count(data.demand.modulesRequiredInProgress)}</td></tr>
                  <tr><td>{strings.InventoryModulesProcured()}</td><td className="num">{count(data.demand.modulesProcured)}</td></tr>
                  <tr><td>{strings.InventoryModulesAllocatedToSites()}</td><td className="num">{count(data.demand.modulesAllocatedToSites)}</td></tr>
                  <tr><td>{strings.InventoryModulesRemaining()}</td><td className="num">{count(data.demand.modulesRemaining)}</td></tr>
                  <tr>
                    <td>{strings.InventorySurplusDeficit()}</td>
                    <td className="num">{data.demand.surplus < 0 ? <span className="neg">{count(data.demand.surplus)}</span> : count(data.demand.surplus)}</td>
                  </tr>
                  <tr>
                    <td>{strings.InventorySitesByAllocation()}</td>
                    <td className="num">{count(data.demand.complete)} / {count(data.demand.partial)} / {count(data.demand.unallocated)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="note">{strings.InventoryRequirementBasisNote()}</div>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryByWarehouse()}</h2>
              <div className="scroll-x">
                <table className="inv">
                  <thead>
                    <tr>
                      <th>{strings.InventoryWarehouse()}</th>
                      <th className="num">{strings.InventoryLineItems()}</th>
                      <th className="num">{strings.InventoryUnitsOnHand()}</th>
                      <th className="num">{strings.InventoryTotalValue()}</th>
                      <th className="num">{strings.InventoryUtilization()}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byWarehouse.map((w) => (
                      <tr key={w.warehouseId}>
                        <td>{w.code}</td>
                        <td className="num">{count(w.lineItems)}</td>
                        <td className="num">{count(w.unitsOnHand)}</td>
                        <td className="num">{money(w.value)}</td>
                        <td className="num">{w.utilizationPct == null ? orDash(undefined) : `${w.utilizationPct}%`}</td>
                      </tr>
                    ))}
                    {!data.byWarehouse.length && <EmptyRow colSpan={5} />}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryByPo()}</h2>
              <div className="scroll-x">
                <table className="inv">
                  <thead>
                    <tr>
                      <th>{strings.InventoryPo()}</th>
                      <th className="num">{strings.InventoryPalletsExpected()}</th>
                      <th className="num">{strings.InventoryPalletsLoaded()}</th>
                      <th className="num">{strings.InventoryPalletsOutstanding()}</th>
                      <th className="num">{strings.InventoryModulesRemaining()}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byPo.map((p) => (
                      <tr key={p.lotId}>
                        <td><Link to={`/inventory/lots/${p.lotId}`}>{p.poNumber || p.contractNumber}</Link></td>
                        <td className="num">{count(p.palletsExpected)}</td>
                        <td className="num">{count(p.palletsLoaded)}</td>
                        <td className="num">{p.palletsOutstanding ? <span className="neg">{count(p.palletsOutstanding)}</span> : count(0)}</td>
                        <td className="num">{count(p.modulesRemaining)}</td>
                      </tr>
                    ))}
                    {!data.byPo.length && <EmptyRow colSpan={5} />}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryByStatus()}</h2>
              <table className="inv">
                <thead>
                  <tr>
                    <th>{strings.Status()}</th>
                    <th className="num">{strings.InventoryLineItems()}</th>
                    <th className="num">{strings.InventoryTotalUnits()}</th>
                    <th className="num">{strings.InventoryTotalValue()}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byStatus.filter((s) => s.lineItems > 0).map((s) => (
                    <tr key={s.status}>
                      <td><Link to={`/inventory/pallet-inventory?status=${s.status}`}>{inventoryPalletStatusLabel(s.status)}</Link></td>
                      <td className="num">{count(s.lineItems)}</td>
                      <td className="num">{count(s.units)}</td>
                      <td className="num">{money(s.value)}</td>
                    </tr>
                  ))}
                  {!data.byStatus.some((s) => s.lineItems > 0) && <EmptyRow colSpan={4} />}
                </tbody>
              </table>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryLogistics()}</h2>
              <table className="inv">
                <tbody>
                  <tr><td>{strings.InventoryInboundLoads()}</td><td className="num">{count(data.logistics.inboundLoads)}</td></tr>
                  <tr><td>{strings.InventoryInboundUnits()}</td><td className="num">{count(data.logistics.inboundUnits)}</td></tr>
                  {data.logistics.byStatus.filter((s) => s.count > 0).map((s) => (
                    <tr key={s.status}><td className="muted">&nbsp;&nbsp;{inventoryShipmentStatusLabel(s.status)}</td><td className="num">{count(s.count)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryQualityFlags()}</h2>
              <table className="inv">
                <tbody>
                  <tr><td>{strings.InventoryDamagedUnits()}</td><td className="num">{count(data.flags.damagedUnits)}</td></tr>
                  <tr><td>{strings.InventoryStatusRmaReturn()}</td><td className="num">{count(data.flags.rmaLineItems)}</td></tr>
                  <tr><td>{strings.InventoryStatusLost()}</td><td className="num">{count(data.flags.lostLineItems)}</td></tr>
                  <tr><td>{strings.InventorySerialsPending()}</td><td className="num">{count(data.flags.serialsPendingLineItems)}</td></tr>
                  <tr>
                    <td>{strings.InventoryPalletsOnInactiveSites()}</td>
                    <td className="num">{data.flags.palletsOnInactiveSites ? <span className="neg">{count(data.flags.palletsOnInactiveSites)}</span> : count(0)}</td>
                  </tr>
                  <tr><td>{strings.InventoryProvisionalPallets()}</td><td className="num">{count(data.flags.provisionalPallets)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryWarehouseCost()}</h2>
              <table className="inv">
                <tbody>
                  <tr><td>{strings.InventoryInvoicedToDate()}</td><td className="num">{money(data.cost.invoicedTotal, true)}</td></tr>
                  <tr><td>{strings.InventoryUnpaidInvoices()}</td><td className="num">{count(data.cost.invoicesUnpaid)}</td></tr>
                  <tr><td>{strings.InventoryBalanceDue()}</td><td className="num">{money(data.cost.balanceDue, true)}</td></tr>
                  <tr><td>{strings.InventoryDocumentedDomesticContent()}</td><td className="num">{count(data.procurement.documentedDomesticContentModules)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </>}
      </PageBody>
    </InventoryPageStyled>
  );
};
